import { RefObject, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { ICategoryReport, ICategoryReportItemUser, ICategoryReportTasks } from "../types";
import { getApiLink } from "../../../functions/getApiLink";
import { getBearer } from "../../../functions/getBearer";
import { categoryTimesheetColumnWidths, createCategoryTimesheetTable } from "../../../components/excel/CategoryTimesheet";
import { MonthNumber } from "../../../constants/MonthNumber";
import { useTranslation } from "react-i18next";
import { useAnalyticsData } from "./useAnalyticsData";
import TimePie from "./TimePie";
import ExcelJS from 'exceljs';
import html2canvas from "html2canvas";
import i18next from "i18next";
import { formatNumber } from './formatValue';
import { columnNumberToLetter } from "./table";
import { PopupContext } from "../../../App";
import { translateCategorySlug } from "../../../functions/translateCategorySlug";

interface IAnalyticsItem {
	data: ICategoryReport
}

const accentColor = "EF3129", fontName = "Calibri";

const useExport = () => {

	const setPopup: any = useContext(PopupContext);
	const { globalAnalyticsData } = useAnalyticsData();

	const handleExport = useCallback(async (categoriesTimeImage: RefObject<HTMLDivElement>, employeesTimeImage: RefObject<HTMLDivElement>, data: ICategoryReport) => {
		
		// Основные переменные
		const workbook = new ExcelJS.Workbook(),
			  { t } = i18next,
			  tr = (field: string) => t("analytics_export." + field),
			  [fromYear, fromMonth, toYear, toMonth] = globalAnalyticsData.date;

		setPopup({popup: "export-generating-popup", data: t("analytics_export.generating_file")});

		// Разбивка по задачам запрашивается только на экспорт: на экране нужны
		// итоги, а задач за период бывает на порядок больше. Фильтры берём те
		// же, которыми построен отчёт, иначе суммы разойдутся.
		let taskDetails: ICategoryReportTasks | null = null;

		if (globalAnalyticsData.categoriesRequest) {
			try {
				getBearer("post");
				const response = await axios.post(
					getApiLink("/api/analytics/categories/reports/tasks/"),
					globalAnalyticsData.categoriesRequest
				);
				taskDetails = response.data as ICategoryReportTasks;
			} catch (error) {
				// Итоговые листы всё равно отдаём — лучше отчёт без детализации,
				// чем ничего, но молчать об этом нельзя.
				console.error(error);
				toast.error(t("errors.500"));
			}
		}

		// Создание страницы Hours
		const worksheet = workbook.addWorksheet(tr("Hours"));

		// Функция для написания заголовка
		const createHeader = (worksheet: ExcelJS.Worksheet, title?: string) => {
			worksheet.getCell('B2').value = title ?? tr("categories_title");
			worksheet.getCell('B2').font = { name: fontName, size: 22, bold: true };
			worksheet.getCell('B2').alignment = { horizontal: "left", vertical: 'middle' };
		};

		// Функция для написания параметров страницы (имя и период)
		const createField = (worksheet: ExcelJS.Worksheet, label: string, value: string, row: number) => {
			worksheet.mergeCells(`B${row}:C${row}`);
			worksheet.getCell(`B${row}`).value = label;
			worksheet.getCell(`B${row}`).font = { name: fontName, size: 14, bold: true };
			worksheet.getCell(`B${row}`).alignment = { vertical: 'middle' };

			worksheet.getCell(`D${row}`).value = value;
			worksheet.getCell(`D${row}`).font = { name: fontName, size: 12 };
			worksheet.getCell(`D${row}`).alignment = { vertical: 'middle' };
		};




		// Функции для создания изображения графика
		const addImageToSheet = async (worksheet: ExcelJS.Worksheet, imageRef: React.RefObject<HTMLDivElement>, position: any) => {
			if (imageRef.current) {
				const imageUrl = await captureImage(imageRef.current);
				const imageBuffer = await fetch(imageUrl).then(res => res.arrayBuffer());
				const imageId = workbook.addImage({
					buffer: imageBuffer,
					extension: "png",
				});
				worksheet.addImage(imageId, position);
			}
		};

		const captureImage = async (element: HTMLElement): Promise<string> => {
			const canvas = await html2canvas(element, {
				scale: 4
			});
			return canvas.toDataURL('image/png');
		};

		// Формирования мета-данных файла
		workbook.creator = 'IC-App';
		workbook.lastModifiedBy = 'IC-App';
		workbook.properties.date1904 = false;

		// Функция для создания обводки
		function setCellBorder(worksheet: ExcelJS.Worksheet, cellAddress: string | number, cellLastIndex?: number) {
			if (cellLastIndex) {
				worksheet.getCell(cellAddress, cellLastIndex).border = {
					top: { style: 'thin', color: { argb: '000000' } },
					left: { style: 'thin', color: { argb: '000000' } },
					bottom: { style: 'thin', color: { argb: '000000' } },
					right: { style: 'thin', color: { argb: '000000' } },
				};
			} else {
				worksheet.getCell(cellAddress).border = {
					top: { style: 'thin', color: { argb: '000000' } },
					left: { style: 'thin', color: { argb: '000000' } },
					bottom: { style: 'thin', color: { argb: '000000' } },
					right: { style: 'thin', color: { argb: '000000' } },
				};
			}
		}
	
		// Функция для создания таблицы
		const createTable = (worksheet: ExcelJS.Worksheet, display: "categories" | "employees", initCell: { col: number, row: number }) => {

			const formatToFixed = 1;

			if (data) {
				const startRow = initCell.row, startCell = initCell.col;

				// Задавания размера колонок
				worksheet.getColumn(startCell).width = 5;
				worksheet.getColumn(startCell + 1).width = 23;

				// Шапка таблицы
				worksheet.mergeCells([startRow, startCell, startRow, startCell + 1]);
				worksheet.getCell(startRow, startCell).value = 'iC Ukraine Internal';
				worksheet.getCell(startRow, startCell).font = { name: fontName, size: 10, family: 0, color: { argb: "FFFFFF" }, bold: true };
				worksheet.getCell(startRow, startCell).alignment = { horizontal: 'center', vertical: 'middle' };
				worksheet.getCell(startRow, startCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
				setCellBorder(worksheet, startRow, startCell);

				worksheet.getCell(startRow + 1, startCell).value = 'No';
				worksheet.getCell(startRow + 1, startCell).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
				worksheet.getCell(startRow + 1, startCell).alignment = { horizontal: 'center', vertical: 'middle' };
				worksheet.getCell(startRow + 1, startCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
				setCellBorder(worksheet, startRow + 1, startCell);

				worksheet.getCell(startRow + 1, startCell + 1).value = display === "categories" ? tr("categories") : tr("names");
				worksheet.getCell(startRow + 1, startCell + 1).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
				worksheet.getCell(startRow + 1, startCell + 1).alignment = { horizontal: 'center', vertical: 'middle' };
				worksheet.getCell(startRow + 1, startCell + 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
				setCellBorder(worksheet, startRow + 1, startCell + 1);

				worksheet.mergeCells([startRow, startCell + 2, startRow + 1, startCell + 2]);
				worksheet.getCell(startRow, startCell + 2).value = tr("Hours");
				worksheet.getCell(startRow, startCell + 2).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
				worksheet.getCell(startRow, startCell + 2).alignment = { horizontal: 'center', vertical: 'middle' };
				worksheet.getCell(startRow, startCell + 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
				setCellBorder(worksheet, startRow, startCell + 2);

				let totalValue = 0;
				
				// Тело таблицы
				[display === "employees" ? [...globalAnalyticsData.categoriesTimeData.employees] : [...globalAnalyticsData.categoriesTimeData.categories]][0].forEach((item, index) => {
					worksheet.getCell(startRow + 2 + index, startCell).value = index + 1;
					worksheet.getCell(startRow + 2 + index, startCell).alignment = { horizontal: 'center', vertical: 'middle' };
					worksheet.getCell(startRow + 2 + index, startCell).font = { name: fontName, size: 10, color: { argb: "ffffff" } };
					worksheet.getCell(startRow + 2 + index, startCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: item.color.slice(1) } };
					worksheet.getCell(startRow + 2 + index, startCell).border = {
						top: { style: index === 0 ? 'thin' : 'hair', color: { argb: '000000' } },
						bottom: { style: 'hair', color: { argb: '000000' } },
						left: { style: 'thin', color: { argb: '000000' } },
					};

					worksheet.getCell(startRow + 2 + index, startCell + 1).value = display === "employees" ? item.name : translateCategorySlug(t, item.name);
					worksheet.getCell(startRow + 2 + index, startCell + 1).alignment = { wrapText: true, vertical: 'middle' };
					worksheet.getCell(startRow + 2 + index, startCell + 1).font = { name: fontName, size: 10 };
					worksheet.getCell(startRow + 2 + index, startCell + 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "ebebeb" } };
					worksheet.getColumn(startCell + 1).width = (display === "employees" ? item.name : translateCategorySlug(t, item.name)).length + 5;
					worksheet.getCell(startRow + 2 + index, startCell + 1).border = {
						top: { style: index === 0 ? 'thin' : 'hair', color: { argb: '000000' } },
						bottom: { style: 'hair', color: { argb: '000000' } },
						left: { style: 'hair', color: { argb: '000000' } },
					};

					worksheet.getCell(startRow + 2 + index, startCell + 2).value = Number(formatNumber(item.value, formatToFixed));
					worksheet.getCell(startRow + 2 + index, startCell + 2).numFmt = `0.${"0".repeat(formatToFixed)}`;
					worksheet.getCell(startRow + 2 + index, startCell + 2).font = { name: fontName, size: 10 };
					worksheet.getCell(startRow + 2 + index, startCell + 2).alignment = { wrapText: true, vertical: 'middle', horizontal: "center" };
					worksheet.getCell(startRow + 2 + index, startCell + 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "FFFFFF" } };
					worksheet.getCell(startRow + 2 + index, startCell + 2).border = {
						top: { style: index === 0 ? 'thin' : 'hair', color: { argb: '000000' } },
						bottom: { style: 'hair', color: { argb: '000000' } },
						right: { style: 'thin', color: { argb: '000000' } },
						left: { style: 'hair', color: { argb: '000000' } },
					};

					totalValue += item.value;
				})
				
				const startCellLetter = columnNumberToLetter(startCell + 2), endRow = startRow + 2 + (display === "employees" ? globalAnalyticsData.categoriesTimeData.employees.length : globalAnalyticsData.categoriesTimeData.categories.length);
				worksheet.mergeCells([endRow, startCell, endRow, startCell + 1]);
				worksheet.getCell(endRow, startCell).value = tr("total_hours");
				worksheet.getCell(endRow, startCell).font = { name: fontName, size: 10, color: { argb: "000000" }, bold: true };
				worksheet.getCell(endRow, startCell).alignment = { vertical: 'middle', horizontal: "center" };
				worksheet.getCell(endRow, startCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "c7c7c7" } };
				worksheet.getCell(endRow, startCell).border = {
					top: { style: 'thin', color: { argb: '000000' } },
					bottom: { style: 'thin', color: { argb: '000000' } },
					left: { style: 'thin', color: { argb: '000000' } },
				};

				worksheet.getCell(endRow, startCell + 2).value = { formula: `SUM(${startCellLetter}${startRow + 2}:${startCellLetter}${endRow - 1})`, result: String(totalValue), date1904: false };
				worksheet.getCell(endRow, startCell + 2).numFmt = `0.${"0".repeat(formatToFixed)}`;
				worksheet.getCell(endRow, startCell + 2).font = { name: fontName, size: 10, color: { argb: "000000" }, bold: true };
				worksheet.getCell(endRow, startCell + 2).alignment = { vertical: 'middle', horizontal: "center" };
				worksheet.getCell(endRow, startCell + 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: "c7c7c7" } };
				worksheet.getCell(endRow, startCell + 2).border = {
					top: { style: 'thin', color: { argb: '000000' } },
					bottom: { style: 'thin', color: { argb: '000000' } },
					right: { style: 'thin', color: { argb: '000000' } },
					left: { style: 'thin', color: { argb: '000000' } },
				};
			}
		};

		// Функция для создания другой таблицы
		const createEmployeesTable = (worksheet: ExcelJS.Worksheet, data: ICategoryReportItemUser, startRow: number) => {

			const formatToFixed = 1, startCell = 2;

			// Установка размера колонок
			worksheet.getColumn(startCell + 1).width = 12;
			worksheet.getColumn(startCell + 2).width = 25;

			// Шапка таблицы
			worksheet.mergeCells([startRow, startCell, startRow, startCell + 3]);
			worksheet.getCell(startRow, startCell).value = data.name;
			worksheet.getCell(startRow, startCell).font = { name: fontName, size: 10, family: 0, bold: true };
			worksheet.getCell(startRow, startCell).alignment = { horizontal: "left", vertical: 'middle' };
			setCellBorder(worksheet, startRow, startCell);

			worksheet.getCell(startRow + 1, startCell).value = 'No';
			worksheet.getCell(startRow + 1, startCell).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
			worksheet.getCell(startRow + 1, startCell).alignment = { horizontal: 'center', vertical: 'middle' };
			worksheet.getCell(startRow + 1, startCell).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
			setCellBorder(worksheet, startRow + 1, startCell);

			worksheet.getCell(startRow + 1, startCell + 1).value = t("timesheet_page.table.project");
			worksheet.getCell(startRow + 1, startCell + 1).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
			worksheet.getCell(startRow + 1, startCell + 1).alignment = { horizontal: 'center', vertical: 'middle' };
			worksheet.getCell(startRow + 1, startCell + 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
			setCellBorder(worksheet, startRow + 1, startCell + 1);

			worksheet.getCell(startRow + 1, startCell + 2).value = t("timesheet_page.table.project_name");
			worksheet.getCell(startRow + 1, startCell + 2).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
			worksheet.getCell(startRow + 1, startCell + 2).alignment = { horizontal: 'center', vertical: 'middle' };
			worksheet.getCell(startRow + 1, startCell + 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
			setCellBorder(worksheet, startRow + 1, startCell + 2);

			worksheet.getCell(startRow + 1, startCell + 3).value = tr("Hours");
			worksheet.getCell(startRow + 1, startCell + 3).font = { name: fontName, size: 10, color: { argb: "FFFFFF" }, bold: true };
			worksheet.getCell(startRow + 1, startCell + 3).alignment = { horizontal: 'center', vertical: 'middle' };
			worksheet.getCell(startRow + 1, startCell + 3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: accentColor } };
			setCellBorder(worksheet, startRow + 1, startCell + 3);

			// Тело таблицы
			let counter = 0, totalValue = 0;
			data.projects.forEach((project, index) => {
				const style = "hair", row = startRow + 2 + index;

				worksheet.getCell(row, startCell).value = index + 1;
				worksheet.getCell(row, startCell).font = { name: fontName, size: 10, family: 0 };
				worksheet.getCell(row, startCell).alignment = { horizontal: 'center', vertical: 'middle' };
				worksheet.getCell(row, startCell).border = {
					left: { style: "thin", color: { argb: '000000' } },
					bottom: { style, color: { argb: '000000' } },
					right: { style, color: { argb: '000000' } },
				};

				worksheet.getCell(row, startCell + 1).value = project.name;
				worksheet.getCell(row, startCell + 1).font = { name: fontName, size: 10, family: 0 };
				worksheet.getCell(row, startCell + 1).alignment = { horizontal: 'center', vertical: 'middle' };
				worksheet.getCell(row, startCell + 1).border = {
					bottom: { style, color: { argb: '000000' } },
					right: { style, color: { argb: '000000' } },
				};

				worksheet.getCell(row, startCell + 2).value = project.description;
				worksheet.getCell(row, startCell + 2).font = { name: fontName, size: 10, family: 0 };
				worksheet.getCell(row, startCell + 2).alignment = { horizontal: 'center', vertical: 'middle' };
				worksheet.getCell(row, startCell + 2).border = {
					bottom: { style, color: { argb: '000000' } },
					right: { style, color: { argb: '000000' } },
				};

				worksheet.getCell(row, startCell + 3).value = Number(formatNumber(project.hours, formatToFixed));
				worksheet.getCell(row, startCell + 3).numFmt = `0.${"0".repeat(formatToFixed)}`;
				worksheet.getCell(row, startCell + 3).font = { name: fontName, size: 10, family: 0, bold: true };
				worksheet.getCell(row, startCell + 3).alignment = { horizontal: 'center', vertical: 'middle' };
				worksheet.getCell(row, startCell + 3).border = {
					bottom: { style, color: { argb: '000000' } },
					right: { style: "thin", color: { argb: '000000' } },
				};

				totalValue += project.hours;
				counter = index;
			})

			// Подвал таблицы
			const cellLetter = columnNumberToLetter(startCell + 3), endRow = startRow + counter + 3 - (data.projects.length === 0 ? 1 : 0);
			if(data.projects.length === 0) {
				worksheet.getCell(endRow, startCell + 3).value = 0;
			} else {
				worksheet.getCell(endRow, startCell + 3).value = { formula: `SUM(${cellLetter}${startRow + 2}:${cellLetter}${endRow - 1})`, result: String(totalValue), date1904: false };
			}
			
			worksheet.getCell(endRow, startCell + 3).numFmt = `0.${"0".repeat(formatToFixed)}`;
			worksheet.getCell(endRow, startCell + 3).font = { name: fontName, size: 10, family: 0, color: { argb: accentColor }, bold: true };
			worksheet.getCell(endRow, startCell + 3).alignment = { horizontal: 'center', vertical: 'middle' };
			worksheet.getCell(endRow, startCell + 3).border = {
				top: { style: "thin", color: { argb: '000000' } },
				bottom: { style: "thin", color: { argb: '000000' } },
				right: { style: "thin", color: { argb: '000000' } },
			};

			worksheet.getCell(endRow, startCell).border = {
				left: { style: "thin", color: { argb: '000000' } },
				top: { style: "thin", color: { argb: '000000' } },
				bottom: { style: "thin", color: { argb: '000000' } }
			};

			worksheet.getCell(endRow, startCell + 1).border = {
				top: { style: "thin", color: { argb: '000000' } },
				bottom: { style: "thin", color: { argb: '000000' } },
			};

			worksheet.getCell(endRow, startCell + 2).border = {
				top: { style: "thin", color: { argb: '000000' } },
				bottom: { style: "thin", color: { argb: '000000' } },
			};

		};

		// Подписи колонок берём из таймшита — лист повторяет именно его
		const timesheetLabels = {
			date: t("timesheet_page.table.date"),
			project: t("timesheet_page.table.project"),
			project_name: t("timesheet_page.table.project_name"),
			category: t("timesheet_page.categories.Category"),
			task: t("timesheet_page.table.task"),
			time: t("timesheet_page.table.time"),
			hours: t("timesheet_page.table.h"),
			total: tr("total"),
		};

		// Записывания данных в странице Hours
		worksheet.mergeCells('B2:G2');
		createHeader(worksheet);
		worksheet.mergeCells(`D4:G4`);
		worksheet.mergeCells(`D5:G5`);
		createField(worksheet, tr("categories") + ":", `${data.categories.map((category, index) => (index === 0 ? "" : " ") + translateCategorySlug(t, category.name))}`, 4);
		createField(worksheet, tr("report_period") + ":", `${t(`${MonthNumber()[fromMonth].translate_code}`)}, ${fromYear} - ${t(`${MonthNumber()[toMonth].translate_code}`)}, ${toYear}`, 5);
		
		// Добавление круговых диаграмм на первую страницу
		await addImageToSheet(worksheet, categoriesTimeImage, {
			tl: { col: 1, row: 7 },
			ext: { width: 280, height: 280 }
		});
		
		await addImageToSheet(worksheet, employeesTimeImage, {
			tl: { col: 11, row: 7 },
			ext: { width: 280, height: 280 }
		});
		
		createTable(worksheet, "categories", { col: 8, row: 8 });
		createTable(worksheet, "employees", { col: 18, row: 8 });

		data.categories.forEach(category => {
			const worksheet = workbook.addWorksheet(translateCategorySlug(t, category.name));

			worksheet.mergeCells('B2:E2');
			createHeader(worksheet);
			worksheet.getColumn("C").width = 23;
			worksheet.mergeCells(`D4:E4`);
			worksheet.mergeCells(`D5:E5`);
			createField(worksheet, tr("categories") + ":", translateCategorySlug(t, category.name), 4);
			createField(worksheet, tr("report_period") + ":", `${t(`${MonthNumber()[fromMonth].translate_code}`)}, ${fromYear} - ${t(`${MonthNumber()[toMonth].translate_code}`)}, ${toYear}`, 5);

			let row = 7;
			category.users.forEach(user => {
				try {
					createEmployeesTable(worksheet, user, row);
					row += 3 + user.projects.length + 1;
				} catch {
					setPopup({popup: "", data: ""});
				}
			})

			// Сразу за листом категории — её же задачи построчно
			const detail = taskDetails?.categories.find(item => item.name === category.name);

			if (detail) {
				// Excel не берёт имя листа длиннее 31 символа
				const sheetName = `${translateCategorySlug(t, category.name)} ${t("timesheet_page.table.timesheet")}`.slice(0, 31);
				const timesheet = workbook.addWorksheet(sheetName);

				timesheet.mergeCells('B2:I2');
				createHeader(timesheet, tr("categories_timesheets_title"));

				// Ширины — как в экспорте таймшита, откуда взяты сами колонки
				Object.entries(categoryTimesheetColumnWidths).forEach(([letter, width]) => {
					timesheet.getColumn(letter).width = width;
				});

				timesheet.mergeCells(`D4:I4`);
				timesheet.mergeCells(`D5:I5`);
				createField(timesheet, tr("categories") + ":", translateCategorySlug(t, category.name), 4);
				createField(timesheet, tr("report_period") + ":", `${t(`${MonthNumber()[fromMonth].translate_code}`)}, ${fromYear} - ${t(`${MonthNumber()[toMonth].translate_code}`)}, ${toYear}`, 5);

				let timesheetRow = 7;
				detail.users.forEach(user => {
					try {
						timesheetRow = createCategoryTimesheetTable(timesheet, user, timesheetRow, timesheetLabels, slug => translateCategorySlug(t, slug));
					} catch {
						setPopup({popup: "", data: ""});
					}
				})
			}
		})

		// Скрипт для экспорта сгенерированного файла
		workbook.xlsx.writeBuffer().then((buffer) => {
			const blob = new Blob([buffer], { type: 'application/octet-stream' }),
			date = new Date(), year = String(date.getFullYear()).slice(2, 4), month = date.getMonth() + 1 > 9 ? String(date.getMonth() + 1) : `0${date.getMonth() + 1}`,
			day = date.getDate() > 9 ? String(date.getDate()) : `0${date.getDate()}`;

			const link = document.createElement('a');
			link.href = URL.createObjectURL(blob);
			link.download = `${year}${month}${day}_Category_Statistic_Report.xlsx`;
			
			setTimeout(() => {
				link.click();
				link.remove();
				setPopup({popup: "", data: ""});
			}, 500);
		});
	}, [globalAnalyticsData.categoriesTimeData.categories, globalAnalyticsData.categoriesTimeData.employees, globalAnalyticsData.date, globalAnalyticsData.categoriesRequest, setPopup]);

	return handleExport;
}

export default function AnalyticsCategoriesItem({ data } : IAnalyticsItem) {

	const [isActive, setIsActive] = useState(true),
	[isHide, setIsHide] = useState(false),
	timeout = useRef<ReturnType<typeof setTimeout> | null>(null),
	{ globalAnalyticsData } = useAnalyticsData(),
	categoriesImage = useRef<HTMLDivElement>(null),
	employeesImage = useRef<HTMLDivElement>(null),
	{ t } = useTranslation(),
	handleExport = useExport();

	useEffect(() => {
		setIsHide(true);
		if(isActive) {
			timeout.current = setTimeout(() => {
				setIsHide(false);
			}, 500)
		}
	}, [isActive]);




	return (
		<div className={`analytics-item${isActive ? " is-active" : ""}${isHide ? " is-hide" : ""}`}>
			<div className="analytics-item__header">
				<button className="analytics-item__target" onClick={() => {if(isHide) setIsActive(true); else if(!isHide) setIsActive(false)}} aria-label="Category statistic report "></button>
				<div className="analytics-item__header-col">
					<h3 className="analytics-item__title">
						Category statistic report 
					</h3>
					<div className="analytics-item__date">{MonthNumber()[globalAnalyticsData.date[1]].translate}, {globalAnalyticsData.date[0]} - {MonthNumber()[globalAnalyticsData.date[3]].translate}, {globalAnalyticsData.date[2]}</div>
				</div>
				<div className="analytics-item__header-col">
					<button className="analytics-item__export btn is-grey is-min is-transparent visible-on-desktop" onClick={() => handleExport(categoriesImage, employeesImage, data)} aria-label={t("employees_admin.table.export")} type="button">
						<span>{t("employees_admin.table.export")}</span>
						<svg width="24" height="24">
							<use href="#download"></use>
						</svg>
					</button>
					<div className="analytics-item__arrow">
						<svg width="10" height="7">
							<use href="#drop-down-arrow"></use>
						</svg>
					</div>
				</div>
			</div>
			<div className="analytics-item__content">
				<div>
					<TimePie data={globalAnalyticsData.categoriesTimeData.categories} pieRef={categoriesImage} is="categories" />
					<TimePie data={globalAnalyticsData.categoriesTimeData.employees} pieRef={employeesImage} is="employees_names" />
					<button className="analytics-item__export btn is-grey is-min is-transparent visible-on-mob" onClick={() => handleExport(categoriesImage, employeesImage, data)} type="button" aria-label={t("employees_admin.table.export")}>
						<span>{t("employees_admin.table.export")}</span>
						<svg width="24" height="24">
							<use href="#download"></use>
						</svg>
					</button>
				</div>
			</div>
		</div>
	)
}